/**
 * CSRF token sources, in priority order:
 *
 * 1. The `X-CSRF-Token` cookie set by Rails' `CsrfCookieEnabled` on every API
 *    response. Always fresh, always same-origin readable.
 * 2. The `<meta name="csrf-token">` tag rendered into the Rails shell page.
 *    Available on Rails-rendered routes and when the SPA boots.
 * 3. A module-level cache populated from the `X-CSRF-Token` response header.
 *    Same-origin JS can always read response headers, even when third-party
 *    cookies are blocked (e.g., when the app is embedded in the Healthie
 *    iframe — see `attachInterceptors`).
 */

let cachedHeaderToken = ''

function readFromCookie(): string {
  const match = document.cookie.match(/(?:^|; )X-CSRF-Token=([^;]*)/)
  return match ? decodeURIComponent(match[1] ?? '') : ''
}

function readFromMeta(): string {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute('content') ?? ''
}

export function getCSRFToken(): string {
  return readFromCookie() || readFromMeta() || cachedHeaderToken
}

/**
 * Cache a token read from an `X-CSRF-Token` response header. Called by the
 * response interceptor on every API response and after a CSRF refresh.
 */
export function cacheHeaderToken(token: string): void {
  cachedHeaderToken = token
}

/** Test-only helper. */
export function resetCachedHeaderToken(): void {
  cachedHeaderToken = ''
}
